"use client";

import { useLayoutEffect, useRef, type TextareaHTMLAttributes } from "react";
import styles from "./SocialPost.module.css";

export type SocialComposerFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  variant?: "title" | "body" | "teaser" | "comment";
};

function fitContent(field: HTMLTextAreaElement) {
  const previousHeight = field.getBoundingClientRect().height;
  field.style.height = "0px";
  const nextHeight = field.scrollHeight;
  field.style.height = `${nextHeight}px`;
  if (previousHeight !== field.getBoundingClientRect().height && document.activeElement === field) {
    field.scrollTop = 0;
  }
}

/** A controlled writing field with the same font and wrapping as published text. */
export function SocialComposerField({ label, variant = "body", className, onInput, rows = 1, ...props }: SocialComposerFieldProps) {
  const field = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (field.current) fitContent(field.current);
  }, [props.value]);

  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    let width = element.getBoundingClientRect().width;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === width) return;
      width = entry.contentRect.width;
      fitContent(element);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return <textarea {...props} ref={field} rows={rows} aria-label={props["aria-label"] ?? label} className={[styles.field, styles[`${variant}Field`], className].filter(Boolean).join(" ")} onInput={(event) => {
    fitContent(event.currentTarget);
    onInput?.(event);
  }} />;
}
